/**
 * Cookie-free page counter (self-hosted Umami), mirroring apps/site/src/layouts/Base.astro.
 *
 * It loads only when VITE_UMAMI_WEBSITE_ID is set at build time. Search and hash are excluded
 * so tokens in verify/reset links never reach analytics, and `scrubPayload` (Umami's `data-before-send` hook)
 * rewrites every page address to its route template, so a transcription id never leaves the browser
 * in a pageview, an event or a referrer.
 */
import type { CaptionPresetId } from '@fjalim/shared'
import { routeTemplate } from './routeTemplates'

export const UMAMI_SCRIPT_URL = 'https://insights.applifyer.com/script.js'

/** The global Umami calls before each send (`data-before-send`). */
export const BEFORE_SEND_NAME = 'fjalimBeforeSend'

type UmamiPayload = Record<string, unknown>

/** An app address reduced to its origin and route template: no ids, query or hash. Empty when it is not an http(s) address. */
export function scrubAppUrl(raw: unknown, base: string): string {
  if (typeof raw !== 'string' || raw === '') return ''
  try {
    const url = new URL(raw, base)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return ''
    const own = new URL(base).origin
    // A page of this app keeps its route template; any other site is reduced to its origin.
    return url.origin === own ? `${url.origin}${routeTemplate(url.pathname)}` : url.origin
  } catch {
    return ''
  }
}

/**
 * Umami's `data-before-send` hook: receives (type, payload) and returns the payload to send, or a falsy value to cancel.
 * It rewrites `url` and `referrer` to route templates, drops the page title (it can carry a file name) and
 * cancels `identify`, which the app never uses.
 */
export function scrubPayload(type: string, payload: UmamiPayload, base: string = globalThis.location?.href ?? 'https://app.fjalim.com/'): UmamiPayload | null {
  if (type === 'identify' || !payload || typeof payload !== 'object') return null
  const next: UmamiPayload = { ...payload }
  if ('url' in next) next.url = scrubAppUrl(next.url, base)
  if ('referrer' in next) next.referrer = scrubAppUrl(next.referrer, base)
  delete next.title
  return next
}

export function initAnalytics(
  websiteId: string | undefined = import.meta.env.VITE_UMAMI_WEBSITE_ID,
  doc: Document = document,
): void {
  const id = websiteId?.trim()
  if (!id) return
  if (doc.querySelector(`script[src="${UMAMI_SCRIPT_URL}"]`)) return

  const script = doc.createElement('script')
  script.defer = true
  script.src = UMAMI_SCRIPT_URL
  script.setAttribute('data-website-id', id)
  script.setAttribute('data-do-not-track', 'true')
  script.setAttribute('data-exclude-search', 'true')
  script.setAttribute('data-exclude-hash', 'true')
  // The hook must exist before the script runs: Umami looks it up on window by name each time it sends.
  ;(globalThis as Record<string, unknown>)[BEFORE_SEND_NAME] = (type: string, payload: UmamiPayload) => scrubPayload(type, payload)
  script.setAttribute('data-before-send', BEFORE_SEND_NAME)
  doc.head.appendChild(script)
}

/**
 * Product-usage events for the cookie-free counter. Nothing personal leaves the browser: no ids, emails,
 * file names or text, and every number is a bucket. `EVENTS` is the allow-list; the types below make
 * an event or property outside it a compile error, and `track` drops anything outside it at runtime.
 */
export type DurationBucket = '<1m' | '1-5m' | '5-15m' | '15-60m' | '>60m' | 'unknown'
export type AmountBucket = '<10' | '10-24' | '25-49' | '50-99' | '100+'

interface EventProps {
  signup: Record<string, never>
  verify: Record<string, never>
  upload: { kind: 'audio' | 'video'; duration: DurationBucket; speakers: boolean }
  transcription_done: { duration: DurationBucket; speakers: boolean }
  editor_open: Record<string, never>
  edit_save: { tab: 'transcript' | 'captions' }
  export: { format: 'txt' | 'txt-timestamps' | 'srt' | 'vtt' | 'json'; preset?: CaptionPresetId }
  render_create: { preset: CaptionPresetId; free: boolean }
  topup_click: { amount: AmountBucket }
  api_key_create: Record<string, never>
}

export type AnalyticsEvent = keyof EventProps

/** The property names each event may carry. Keep in step with `EventProps`; the test checks the keys match. */
export const EVENTS: { [E in AnalyticsEvent]: readonly (keyof EventProps[E] & string)[] } = {
  signup: [],
  verify: [],
  upload: ['kind', 'duration', 'speakers'],
  transcription_done: ['duration', 'speakers'],
  editor_open: [],
  edit_save: ['tab'],
  export: ['format', 'preset'],
  render_create: ['preset', 'free'],
  topup_click: ['amount'],
  api_key_create: [],
}

interface UmamiGlobal {
  track?: (name: string, data?: Record<string, string | number | boolean>) => void
}

/** Sends one event. Does nothing when the counter is not loaded (no website id, blocked, or Do Not Track). */
export function track<E extends AnalyticsEvent>(
  event: E,
  ...[props]: EventProps[E] extends Record<string, never> ? [] : [props: EventProps[E]]
): void {
  const allowed = EVENTS[event] as readonly string[] | undefined
  if (!allowed) return
  const umami = (globalThis as { umami?: UmamiGlobal }).umami
  if (typeof umami?.track !== 'function') return
  const data: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries((props ?? {}) as Record<string, unknown>)) {
    if (allowed.includes(key) && (typeof value === 'string' || typeof value === 'boolean')) data[key] = value
  }
  try {
    umami.track(event, Object.keys(data).length > 0 ? data : undefined)
  } catch {
    // A broken counter must never break the app.
  }
}

/** Length of the audio, in coarse buckets. `unknown` when the browser could not read it. */
export function durationBucket(seconds: number | null | undefined): DurationBucket {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return 'unknown'
  if (seconds < 60) return '<1m'
  if (seconds < 300) return '1-5m'
  if (seconds < 900) return '5-15m'
  if (seconds <= 3600) return '15-60m'
  return '>60m'
}

/** A top-up amount in whole euros, in coarse buckets. */
export function amountBucket(eur: number): AmountBucket {
  if (eur < 10) return '<10'
  if (eur < 25) return '10-24'
  if (eur < 50) return '25-49'
  if (eur < 100) return '50-99'
  return '100+'
}

/** Audio or video, from the MIME type and, when the browser gives none, the extension. */
export function mediaKind(file: { type: string; name: string }): 'audio' | 'video' {
  if (file.type.startsWith('video/')) return 'video'
  if (file.type.startsWith('audio/')) return 'audio'
  return /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(file.name) ? 'video' : 'audio'
}

/**
 * The length of a local file, read from its metadata in the browser; resolves `undefined` when it cannot be read
 * within `timeoutMs`. The file never leaves the browser and only the bucket is reported.
 */
export function probeDuration(file: Blob, timeoutMs = 4000): Promise<number | undefined> {
  return new Promise((resolve) => {
    let url: string | undefined
    let el: HTMLMediaElement | undefined
    const done = (value: number | undefined) => {
      window.clearTimeout(timer)
      if (el) {
        el.removeAttribute('src')
        el.load()
      }
      if (url) URL.revokeObjectURL(url)
      resolve(value)
    }
    const timer = window.setTimeout(() => done(undefined), timeoutMs)
    try {
      url = URL.createObjectURL(file)
      el = document.createElement('video')
      el.preload = 'metadata'
      el.onloadedmetadata = () => done(Number.isFinite(el!.duration) ? el!.duration : undefined)
      el.onerror = () => done(undefined)
      el.src = url
    } catch {
      done(undefined)
    }
  })
}
